<template>
    <div class="relative isolate bg-white px-6 py-24 sm:py-32 lg:px-8">
        <div class="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl" aria-hidden="true">
            <div class="mx-auto aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-primary-200 to-secondary-200 opacity-40"
                style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)" />
        </div>
        <div class="mx-auto max-w-2xl text-center">
            <span
                class="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-semibold text-primary-700 ring-1 ring-primary-600/10">Pricing</span>
            <p class="mt-4 text-pretty text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Choose the
                right plan for you</p>
            <p class="mx-auto mt-4 max-w-2xl text-pretty text-lg text-slate-600">
                Unlock your potential in Arabic with flexible plans for learners and educators.
                Whether you're just starting or looking to advance, our pricing ensures you get the resources and
                support you need to succeed.
            </p>
        </div>
        <div>
            <Tabs :value=value>
                <div class="flex justify-center items-center mx-auto mt-10">
                    <TabList class="rounded-full bg-slate-100 p-1.5">
                        <Tab value="0" as="div" class="flex items-center gap-2 rounded-full px-5 py-2 cursor-pointer">
                            <span class="font-semibold whitespace-nowrap text-sm">30 Minutes</span>
                        </Tab>
                        <Tab value="1" as="div" class="flex items-center gap-2 rounded-full px-5 py-2 cursor-pointer">
                            <span class="font-semibold whitespace-nowrap text-sm">60 Minutes</span>
                        </Tab>
                    </TabList>
                </div>
                <TabPanels>
                    <TabPanel value="0" as="p" class="m-0">
                        <div
                            class="mx-auto mt-10 grid max-w-7lg grid-cols-1 items-stretch gap-6 sm:mt-12 lg:max-w-7xl lg:grid-cols-4">
                            <!-- Pricing tiers -->
                            <div v-for="(tier, tierIdx) in tiers[0]?.tiers" :key="tier.id" :class="[
                                    tier.featured ? 'bg-slate-900' : 'bg-white',
                                    'relative rounded-3xl p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1',
                                    tier.featured ? 'ring-2 ring-accent-400 hover:shadow-xl' : 'ring-1 ring-slate-900/10 shadow-sm hover:shadow-xl']">
                                <span v-if="tier.featured"
                                    class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">Most
                                    Popular</span>
                                <div :class="[tier.featured ? 'bg-white/10 text-accent-400' : 'bg-primary-50 text-primary-600', 'flex size-11 items-center justify-center rounded-xl']">
                                    <AcademicCapIcon class="size-6" aria-hidden="true" />
                                </div>
                                <h3 :id="tier.id"
                                    :class="[tier.featured ? 'text-accent-400' : 'text-primary-600', 'mt-6 text-base/7 font-semibold']">
                                    {{
                                    tier.name }}</h3>
                                <p class="mt-4 flex items-baseline gap-x-2">
                                    <span
                                        :class="[tier.featured ? 'text-white' : 'text-slate-900', 'text-5xl font-semibold tracking-tight']">
                                        ${{ Number(tier.price) }}</span>
                                    <span
                                        :class="[tier.featured ? 'text-slate-400' : 'text-slate-500', 'text-base']">/month</span>
                                </p>
                                <p :class="[tier.featured ? 'text-slate-300' : 'text-slate-600', 'mt-6 text-base/7']">
                                    {{
                                    tier.description
                                    }}</p>
                                <ul role="list"
                                    :class="[tier.featured ? 'text-slate-300' : 'text-slate-600', 'mt-8 space-y-3 text-sm/6 sm:mt-10']">
                                    <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
                                        <CheckIcon
                                            :class="[tier.featured ? 'text-accent-400' : 'text-primary-600', 'h-6 w-5 flex-none']"
                                            aria-hidden="true" />
                                        {{ feature }}
                                    </li>
                                </ul>
                                <router-link :to="{ name: 'courses' }"
                                    :aria-describedby="tier.id"
                                    :class="[tier.featured ? 'bg-primary-500 text-white shadow-sm hover:bg-primary-400 focus-visible:outline-primary-500' : 'text-primary-600 ring-1 ring-inset ring-primary-200 hover:ring-primary-300 focus-visible:outline-primary-600', 'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10']">
                                    Enroll now
                                </router-link>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value="1" as="p" class="m-0">
                        <div
                            class="mx-auto mt-10 grid max-w-7lg grid-cols-1 items-stretch gap-6 sm:mt-12 lg:max-w-7xl lg:grid-cols-4">
                            <!-- Pricing tiers -->
                            <div v-for="(tier, tierIdx) in tiers[1]?.tiers" :key="tier.id" :class="[
                                    tier.featured ? 'bg-slate-900' : 'bg-white',
                                    'relative rounded-3xl p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1',
                                    tier.featured ? 'ring-2 ring-accent-400 hover:shadow-xl' : 'ring-1 ring-slate-900/10 shadow-sm hover:shadow-xl'
                                ]">
                                <span v-if="tier.featured"
                                    class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">Most
                                    Popular</span>
                                <div :class="[tier.featured ? 'bg-white/10 text-accent-400' : 'bg-primary-50 text-primary-600', 'flex size-11 items-center justify-center rounded-xl']">
                                    <AcademicCapIcon class="size-6" aria-hidden="true" />
                                </div>
                                <h3 :id="tier.id"
                                    :class="[tier.featured ? 'text-accent-400' : 'text-primary-600', 'mt-6 text-base/7 font-semibold']">
                                    {{
                                    tier.name }}</h3>
                                <p class="mt-4 flex items-baseline gap-x-2">
                                    <span
                                        :class="[tier.featured ? 'text-white' : 'text-slate-900', 'text-5xl font-semibold tracking-tight']">
                                        ${{ Number(tier.price) }}</span>
                                    <span
                                        :class="[tier.featured ? 'text-slate-400' : 'text-slate-500', 'text-base']">/month</span>
                                </p>
                                <p :class="[tier.featured ? 'text-slate-300' : 'text-slate-600', 'mt-6 text-base/7']">
                                    {{
                                    tier.description
                                    }}</p>
                                <ul role="list"
                                    :class="[tier.featured ? 'text-slate-300' : 'text-slate-600', 'mt-8 space-y-3 text-sm/6 sm:mt-10']">
                                    <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
                                        <CheckIcon
                                            :class="[tier.featured ? 'text-accent-400' : 'text-primary-600', 'h-6 w-5 flex-none']"
                                            aria-hidden="true" />
                                        {{ feature }}
                                    </li>
                                </ul>
                                <router-link :to="{ name: 'courses' }" :aria-describedby="tier.id"
                                    :class="[tier.featured ? 'bg-primary-500 text-white shadow-sm hover:bg-primary-400 focus-visible:outline-primary-500' : 'text-primary-600 ring-1 ring-inset ring-primary-200 hover:ring-primary-300 focus-visible:outline-primary-600', 'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10']">
                                    Enroll now
                                </router-link>
                            </div>
                        </div>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </div>
    </div>
</template>

<script setup>
import { CheckIcon } from '@heroicons/vue/20/solid';
import { AcademicCapIcon } from '@heroicons/vue/24/outline';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { ref } from 'vue';
import { getTiers } from '@/services/tierService';

const value = ref('0');
const tiers = ref({});

getTiers().then((data) => {
    tiers.value = data.data;
});

</script>

<style>
.p-tabpanels {
    background-color: transparent !important;
}

.p-tablist-tab-list {
    background-color: transparent !important;
    border-color: transparent !important;
    gap: 0.25rem;
}

.p-tablist-active-bar {
    display: none !important;
}

.p-tab {
    border-color: transparent !important;
    color: var(--color-slate-500) !important;
}

.p-tab[data-p-active="true"] {
    background-color: var(--color-white) !important;
    color: var(--color-primary-600) !important;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
}
</style>
